import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  signal,
  Signal,
  ViewChildren,
  ViewEncapsulation,
  WritableSignal,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { OverlayContainer, ConnectedPosition, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { SortEvent } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { BehaviorSubject, map, Observable, Subscription, tap } from 'rxjs';
import { Label } from 'src/app/labels/englishLabels';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { MenuAction } from 'src/app/models/menuAction.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { TableColumnTypeEnum, WidthType } from 'src/app/models/tableColumns/tableColumn.interface';
import { ITableRowViewModel, QueryParametersModel } from 'src/app/models/viewModels/tableRowViewModel';
import { QueryParamsService } from 'src/app/services/queryParams/query-params.service';
import { PopupPosition } from '../copy-button/copy-button.component';
import { RowActionClicked } from '../table-row/table-row.component';
import { PaginationOperationEnum } from '../table/table.component';
import { TextPopupComponent } from '../text-popup/text-popup.component';
import { PossibleNaCellComponent } from './cells/possible-na-cell/possible-na-cell.component';
import { PossibleNaMultiValueCellComponent } from './cells/possible-na-multi-value-cell/possible-na-multi-value-cell.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { NotificationsService } from '../../services/infra/notifications/notifications.service';
import { TableOverlaysService } from './services/table-overlays.service';

interface RowElement extends HTMLElement {
  rowIndex: number;
}

interface PrimeTableColumn {
  header: string;
  prop: string;
  type: TableColumnTypeEnum;
  sortKey?: string;
  toCapitalize?: boolean;
  width: number;
  resizable: boolean;
  tooltipDynamicText?: string;
  customCopyButtonTextLabel?: string;
  linkPropertyKey?: string;
  iconSrc?: string;
  tooltipIconText?: string;
  textPopupTitleKey?: string;
  textPopupTextKey?: string;
  iconAlt?: string;
  iconTableClass?: string;
  dateFormat?: string;
  disabledProp?: string;
  withTruncation?: boolean;
  invertSortIcon?: boolean;
  headerTooltip?: string;
}

interface OutsideClicksIgnored {
  disabled: boolean;
  sidePanel: boolean;
  overlay: boolean;
}

enum Direction {
  UP = -1,
  DOWN = 1,
}

@Component({
  selector: 'app-prime-table',
  templateUrl: './prime-table.component.html',
  styleUrls: ['./prime-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [TableOverlaysService],
  styles: [
    `
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        position: -webkit-sticky;
        position: sticky;
        top: 0px;
      }

      @media screen and (max-width: 64em) {
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
          top: 0px;
        }
      }
    `,
  ],
})
export class PrimeTableComponent<T extends ITableRowViewModel> implements OnInit, OnDestroy, AfterViewInit {
  @Input() public withCheckboxSelection?: boolean = true;
  @Input() public tabelWithIcon?: boolean = true;
  @Input() public withSpacerColumn?: boolean = false;

  // This will be used to identify the which query param is relevant to the table and mark the row as selected based on the query param property id
  @Input() public queryParamProperty?: string;

  // The not found text and icon will be used when the table is empty
  @Input() public tableNotFoundText: string;
  @Input() public tableNotFoundIcon: string;

  // Subject is required to be able to update the columns shown and order.
  public columns = input.required<ITableColumn[]>();

  @Input() public data$: BehaviorSubject<T[]>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public rowsPerPage: any;
  @Input() public isLoading: boolean;
  @Input() public sortedColumn$: BehaviorSubject<SortColumn | undefined>;
  @Input() public pageInfo: ITablePageInfo;
  @Input() public withExpandFilters = true;

  // Control used for the bulk action based on query
  @Input() public expandFilterResultControl = new FormControl<boolean>(false);
  @Input() public checkedRowsSupportedActions$ = new BehaviorSubject<MenuAction[]>([]);

  @Input() public withActions?: boolean = true;
  @Input() public withRowSelection = true;
  @Input() public useBuiltInTextPopup = true;

  public unselectOnOutsideClickParams = input<OutsideClicksIgnored, Partial<OutsideClicksIgnored>>(
    { disabled: false, overlay: true, sidePanel: true },
    {
      transform: (v) => ({ disabled: false, overlay: true, sidePanel: true, ...v }),
    }
  );

  @Output() public pageOnChange = new EventEmitter<PaginationOperationEnum>();
  @Output() public rowActionOnClick = new EventEmitter<RowActionClicked>();
  @Output() public checkedRowsUpdate = new EventEmitter<Set<string>>();
  @Output() public rowSelected = new EventEmitter<T>();
  @Output() public rowUnselected = new EventEmitter<void>();
  @Output() public multiCheckOnClick = new EventEmitter<MenuAction>();
  @Output() public textPopupClick = new EventEmitter<T>();

  @ViewChildren('cellRef') cellRefs!: QueryList<ElementRef>;

  // Used for the table header checkbox
  public allCheckedControl = new FormControl<boolean>(false);

  public tableColumnTypeEnum = TableColumnTypeEnum;
  public copyButtonPopupPosition = PopupPosition.Center;
  // Used to set the starting sort order on the table.
  public sortKey: string;
  public sortOrder: number;

  // Any is used because primeNg columns doesn't have a type
  // Used to show the selected columns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectedColumns: Signal<any[]>; // PrimeTableColumn[]

  // The current row id selected, will be used to show the row as highlighted
  public selectedRowId$: Observable<string>;

  public checkedRowsId = new Set<string>();

  protected selectedRow: WritableSignal<Object | null> = signal(null);

  // Track when a row was last selected to prevent immediate unselection from click-outside events
  private _lastRowSelectionTime = 0;

  protected Direction = Direction;

  public hoveredRowId: string | null = null;
  @ViewChild('usersOverlayContent') private usersOverlayContent?: ElementRef<HTMLElement>;

  private readonly truncatedMap = new Map<string, boolean>();

  private readonly _queryParamsSubscription$: Subscription;
  private _firstElement: number;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _router: Router,
    private readonly _queryParamsService: QueryParamsService,
    private readonly _renderer: Renderer2,
    private readonly _self: ElementRef,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _sidePanel: SidePanelService,
    private readonly _overlay: OverlayContainer,
    private readonly _notificationService: NotificationsService,
    public readonly overlays: TableOverlaysService
  ) {
    this.selectedColumns = computed(() => {
      return this.columns()
        .filter((colum) => !colum.hidden)
        .map((column) => this.getPrimeTableColumns(column));
    });

    effect(() => this.selectedColumns() && this.checkTruncation());
  }

  public ngOnDestroy(): void {
    this._queryParamsSubscription$?.unsubscribe();
  }

  public ngAfterViewInit(): void {
    // We don't have access to internal prime-ng paginator, so we need to update the
    // aria-labels programmatically at runtime
    this._updateAriaLabel('button.p-paginator-next', 'Next page');
    this._updateAriaLabel('button.p-paginator-prev', 'Previous page');
  }

  public openDialog(title: string, text: string): void {
    this._matDialog.open(TextPopupComponent, {
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
      width: '700px',
      autoFocus: false,
      data: { title: title, text: text },
    });
  }

  public onTextPopupCellClick(row: T, col: ITableColumn): void {
    this.textPopupClick.emit(row);
    if (this.useBuiltInTextPopup) {
      const titleKey = col?.textPopupTitleKey as keyof T | undefined;
      const textKey = col?.textPopupTextKey as keyof T | undefined;
      const title = titleKey ? ((row as unknown as Record<string, unknown>)[titleKey as string] as string) : '';
      const text = textKey ? ((row as unknown as Record<string, unknown>)[textKey as string] as string) : '';
      this.openDialog(title, text);
    }
  }

  public onIconError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/common/file.svg';
  }

  public ngOnInit(): void {
    // Set initial sort for table UI
    this.sortKey = this.sortedColumn$?.value?.column.sortKey ?? '';
    this.sortOrder = this.sortedColumn$?.value?.isDesc ? -1 : 1;

    this._firstElement = -1;

    this.data$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      setTimeout(() => this.checkTruncation(), 0);
    });

    this.allCheckedControl.valueChanges
      .pipe(
        tap((value: boolean | null) => {
          if (value) {
            this.data$.value.forEach((row) => this.checkedRowsId.add(row.rowId));
          } else {
            this.checkedRowsId.clear();
          }
          this.checkedRowsUpdate.emit(this.checkedRowsId);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();

    this._updateSelectedRowByQueryParams();
  }

  public onEscKey(event: Event) {
    (event.target as HTMLElement)?.blur();
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.withRowSelection) {
      return;
    }
    this.selectedRow.set(null);
    this.rowUnselected.emit();
  }

  public onRowFocus(event: Event, focusedRow: T) {
    if (!this.withRowSelection) {
      return;
    }

    this.onRowClicked(event, focusedRow);
  }

  public selectRowByArrowKey(event: Event, direction: Direction) {
    if (!this.withRowSelection) {
      return;
    }

    const rowData = this._toRowData(<RowElement>event.target, direction);
    if (rowData) {
      this.onRowClicked(event, rowData);
    }
  }

  public onRowClicked(event: Event, rowData: T) {
    if (!this.withRowSelection) {
      return;
    }

    this.selectedRow.set(rowData);
    this._lastRowSelectionTime = Date.now();

    const newIndex = this._getNewRowIndex(event);

    if (newIndex !== null) {
      this.rowSelected.emit(rowData);
    }
  }

  public clickedOutside(event: Event): void {
    if (!this.withRowSelection) {
      return;
    }

    // Ignore click-outside events that occur within 200ms of row selection
    // This prevents the side panel from closing immediately when clicking a table row
    if (Date.now() - this._lastRowSelectionTime < 200) {
      return;
    }

    const clickTarget = event.target as HTMLElement;

    if (this._self.nativeElement.contains(clickTarget)) {
      return;
    }

    if (!clickTarget.isConnected) {
      return;
    }

    let { disabled, sidePanel, overlay } = this.unselectOnOutsideClickParams();
    let ignoreClickInSidePanel = sidePanel && this._sidePanel.isInsideSidePanel(event.target as HTMLElement);
    let ignoreClickInOverlay = overlay && this._isInsideCdkOverlay(event.target as HTMLElement);
    let ignoreClickInNotification = this._notificationService.isInsideNotification(event.target as HTMLElement);

    if (disabled || ignoreClickInSidePanel || ignoreClickInOverlay || ignoreClickInNotification) {
      return;
    }

    const currentlySelectedRow = this.selectedRow();
    this.selectedRow.set(null);

    if (currentlySelectedRow) {
      this.rowUnselected.emit();
    }
  }

  private _isInsideCdkOverlay(element: HTMLElement | null): boolean {
    if (!element) return false;

    if (this._overlay.getContainerElement().contains(element)) {
      return true;
    }

    let current: HTMLElement | null = element;
    while (current) {
      const classList = current.classList;
      if (
        classList?.contains('cdk-overlay-pane') ||
        classList?.contains('cdk-overlay-container') ||
        classList?.contains('mat-mdc-select-panel') ||
        classList?.contains('mat-mdc-option') ||
        classList?.contains('mat-mdc-dialog-container') ||
        current.tagName?.toLowerCase() === 'mat-option' ||
        current.tagName?.toLowerCase() === 'mat-dialog-container'
      ) {
        return true;
      }
      current = current.parentElement;
    }

    return false;
  }

  public onLazyLoad(event: TableLazyLoadEvent) {
    const sortKey = event.sortField;
    const isDesc = event.sortOrder !== 1;

    if (
      sortKey &&
      event.sortOrder &&
      (sortKey !== this.sortedColumn$?.value?.column.sortKey || isDesc !== this.sortedColumn$?.value?.isDesc)
    ) {
      const foundColumn = this.selectedColumns().find((column) => column.sortKey === sortKey);
      if (foundColumn && this.sortedColumn$) {
        this.sortedColumn$.next({
          column: foundColumn,
          isDesc: isDesc,
        });
      }
    } else if (event.first !== undefined) {
      const paginationOperation =
        event.first > this._firstElement ? PaginationOperationEnum.next : PaginationOperationEnum.prev;
      this._firstElement = event.first;

      this.pageOnChange.emit(paginationOperation);
    }
  }

  public onSortClick(event: SortEvent) {
    if (!this.isLoading) {
      const sortKey = event.field;
      const isDesc = event.order !== 1;

      const foundColumn = this.selectedColumns().find((column) => column.sortKey === sortKey);

      if (foundColumn) {
        //TODO implmenet the sort asc for 'first seen' column
        this.sortedColumn$.next({
          column: foundColumn,
          isDesc: isDesc,
        });
      }
    }
  }

  public actionOnClick(action: MenuAction, row: ITableRowViewModel) {
    if (this.withActions) {
      this.rowActionOnClick.emit({
        row: new BehaviorSubject<ITableRowViewModel>(row),
        action: action,
      });

      if (action.shouldOpenDialog) {
        this._addRowIdToUrl();
      }
    }
  }

  public onCheckboxClick(rowId: string): void {
    if (this.checkedRowsId.has(rowId)) {
      this.checkedRowsId.delete(rowId);
    } else {
      this.checkedRowsId.add(rowId);
    }

    if (this.checkedRowsId.size === this.pageInfo.currSize) {
      this.allCheckedControl.setValue(true);
    }
    this.checkedRowsUpdate.emit(this.checkedRowsId);
  }

  public isTruncated(rowId: string, colProp: string): boolean {
    return this.truncatedMap.get(`${rowId}_${colProp}`) ?? false;
  }

  public checkTruncation(): void {
    this._cdr.detectChanges();

    this.cellRefs.forEach((cellRef) => {
      let element: HTMLElement | null = null;
      let rowId: string | null = null;
      let colKey: string | null = null;
      if (cellRef.nativeElement) {
        element = cellRef.nativeElement;
        rowId = element!.getAttribute('data-row-id');
        colKey = element!.getAttribute('data-col-key');
        this._updateTruncatedMap(element, rowId, colKey);
      }
      if (cellRef instanceof PossibleNaCellComponent || cellRef instanceof PossibleNaMultiValueCellComponent) {
        const element: HTMLElement | null = cellRef.hostElement.nativeElement;
        rowId = element?.dataset['rowId']!;
        colKey = element?.dataset['colKey']!;
        this._updateTruncatedMap(element, rowId, colKey);
      }
    });
  }

  private getPrimeTableColumns(column: ITableColumn) {
    return {
      header: Label.get(column.labelPath),
      prop: column.propertyKey,
      type: column.colType,
      sortKey: column.sortKey,
      toCapitalize: column.toCapitalize,
      width: column.width,
      resizable: column.widthType === WidthType.Pixel,
      tooltipDynamicText: column.tooltipDynamicText,
      customCopyButtonTextLabel: column.customCopyButtonTextLabel,
      withCopyIcon: column.withCopyIcon,
      linkPropertyKey: column.linkPropertyKey,
      textPopupTextKey: column.textPopupTextKey,
      textPopupTitleKey: column.textPopupTitleKey,
      iconSrc: column.iconSrc,
      tooltipIconText: column.tooltipIconText,
      iconOnly: column.iconOnly,
      textColor: column.textColor,
      iconAlt: column.iconAlt,
      iconTableClass: column.iconTableClass,
      dateFormat: column.dateFormat,
      disabledProp: column.disabledProp,
      isSortableDisabled: column.isSortableDisabled,
      withTruncation: column.withTruncation,
      invertSortIcon: column.invertSortIcon,
      headerTooltip: column.descriptionLabelPath ? Label.get(column.descriptionLabelPath) : '',
    } as PrimeTableColumn;
  }

  private _updateAriaLabel(selector: string, label: string) {
    const buttons = <HTMLElement[]>this._self.nativeElement.querySelectorAll(selector);
    buttons.forEach((button) => this._renderer.setAttribute(button, 'aria-label', label));
  }

  public usersOverlayPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -2 },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 2 },
  ];

  public openUsersOverlay(origin: CdkOverlayOrigin, users: unknown) {
    if (Array.isArray(users)) {
      this.overlays.users.openOverlay(origin, users as string[]);
      this.onUsersOverlayAttached();
    }
  }

  public scheduleUsersOverlayClose() {
    this.overlays.users.scheduleClose();
  }

  public cancelUsersOverlayClose() {
    this.overlays.users.cancelClose();
  }

  public closeUsersOverlay() {
    this.overlays.users.close();
  }

  public onUsersOverlayAttached() {
    const contentEl = this.usersOverlayContent?.nativeElement;
    if (!contentEl) return;
    const isScrollable = contentEl.scrollHeight > contentEl.clientHeight + 1;
    contentEl.classList.toggle('is-scrollable', isScrollable);
  }

  public openPermOverlay(origin: CdkOverlayOrigin, title: string, items: unknown) {
    const list = Array.isArray(items) ? (items as { icon?: string; name: string }[]) : [];
    this.overlays.perm.openOverlay(origin, title, list);
  }

  public schedulePermOverlayClose(delay = 0) {
    this.overlays.perm.scheduleClose(delay);
  }

  public cancelPermOverlayClose() {
    this.overlays.perm.cancelClose();
  }

  public closePermOverlay() {
    this.overlays.perm.close();
  }

  private _getNewRowIndex(event: Event): number | null {
    if (event.type === 'keydown') {
      const keyboardEvent = event as KeyboardEvent;
      const currentRowIndex = (event.target as RowElement).rowIndex - 1;

      if (keyboardEvent.code === 'ArrowUp') {
        return Math.max(0, currentRowIndex - 1);
      } else if (keyboardEvent.code === 'ArrowDown') {
        return Math.min(this.data$.value.length - 1, currentRowIndex + 1);
      }
    } else if (event.type === 'click') {
      const closestRow = (event.target as HTMLElement)?.closest('tr'); // get the closest <tr> element
      const clickedRowIndex = closestRow ? closestRow.rowIndex - 1 : null;
      return clickedRowIndex !== null && clickedRowIndex >= 0 ? clickedRowIndex : null;
    }

    return null;
  }

  private _toRowData(rowElement: RowElement, direction: Direction): T {
    const focusedRowIndex = rowElement.rowIndex + direction - 1; // Row indices are 1-based in p-table

    const rows = this.data$.value;
    const safeIndex = focusedRowIndex.clamp(0, rows.length - 1);
    return rows[safeIndex];
  }

  private _updateTruncatedMap(element: HTMLElement | null, rowId: string | null, colKey: string | null): void {
    if (rowId && colKey) {
      const isTruncated = element!.scrollWidth > element!.clientWidth;
      this.truncatedMap.set(`${rowId}_${colKey}`, isTruncated);
    }
  }

  private _addRowIdToUrl(queryParametersOnClick?: QueryParametersModel) {
    if (!queryParametersOnClick) {
      queryParametersOnClick = {};
    }

    this._router.navigate([], {
      queryParams: queryParametersOnClick,
      queryParamsHandling: 'merge',
    });
  }

  private _updateSelectedRowByQueryParams() {
    const queryParamProperty = this.queryParamProperty;
    if (!queryParamProperty) return;

    this.selectedRowId$ = this._queryParamsService.toNavigate$.pipe(
      map((navigationParams) =>
        Object.keys(navigationParams).includes(queryParamProperty) ? Object(navigationParams)[queryParamProperty] : ''
      )
    );
  }
}
